// Copyright (c) 2025 WiseTime. All rights reserved.

import { Observable, of } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { StatusCode } from "grpc-web"
import { isGrpcError } from "../../../lib/rpcutils"
import { grpc } from "../../../app/rpc/executor"
import { showSnackBar } from "../../snackbar/actions"
import { timesheetFieldClient } from "../../../app/rpc/client"
import * as TimelineSlice from "../../timeline/slices/timeline"
import * as Actions from "../actions"
import {
  DeleteTimesheetFieldRequest,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"
import { Empty } from "google-protobuf/google/protobuf/empty_pb"

type DeleteTimesheetFieldRpc = (request: DeleteTimesheetFieldRequest) => Observable<Empty>

export const deleteTimesheetField = (deleteTimesheetFieldRpc: DeleteTimesheetFieldRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.deleteTimesheetField.match),
      map(action => action.payload),
      mergeMap(payload =>
        deleteTimesheetFieldRpc(payload.request).pipe(
          map(() => {
            if (payload.onSuccess) {
              payload.onSuccess()
            }
            return TimelineSlice.deleteTimesheetField({
              id: payload.request.getId(),
            })
          }),
          catchError(e => handleError(e, payload)),
        )
      ),
    )

const handleError = (e: unknown, payload: Actions.Payload<DeleteTimesheetFieldRequest>): Observable<Action> => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      return of(TimelineSlice.deleteTimesheetField({
        id: payload.request.getId(),
      }))
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({
          id: "msg_delete_timesheet_field_permission_denied",
          message: "Failed to delete timesheet field. You do not have permission.",
        }),
        messageType: "ERROR",
      }))
    }
  }
  return of(showSnackBar({
    message: t({
      id: "msg_delete_timesheet_field_failed",
      message: "Failed to delete timesheet field. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
    }),
    messageType: "ERROR",
  }))
}

const deleteTimesheetFieldRpc = (request: DeleteTimesheetFieldRequest): Observable<Empty> =>
  grpc(() => timesheetFieldClient.deleteTimesheetField(request, null))

export default deleteTimesheetField(deleteTimesheetFieldRpc)
